// app/shared/hooks/useDataFetchingHook.ts
"use client";

import { CustomError,useErrorNotificationHook } from './useErrorNotificationHook';
import { useLoadingHook, useNamedLoadingHook } from './useLoadingHook';

interface UseDataFetchingParams {
  /**
   * Boolean indicating if the data is loading
   */
  isLoading: boolean;
  
  /**
   * Boolean indicating if an error occurred
   */
  isError: boolean;
  
  /**
   * The error object if an error occurred
   */
  error: CustomError | unknown | null;
  
  /**
   * Error message title to display
   */
  errorMessage: string;
  
  /**
   * Optional name for tracking specific loading states
   */
  loadingName?: string;
}

/**
 * Combined hook for managing loading state and error notifications
 * 
 * This hook wraps useLoadingHook and useErrorNotificationHook to provide
 * a simplified API for handling data fetching states.
 */
export const useDataFetchingHook = ({
  isLoading,
  isError,
  error,
  errorMessage,
  loadingName,
}: UseDataFetchingParams) => {
  // Convert unknown error to CustomError
  const typedError = error as CustomError | null;
  
  // Update global loading state
  useLoadingHook(isLoading);
  
  // Update named loading state if provided.
  // The hook is called unconditionally, but its effect is conditional.
  useNamedLoadingHook(loadingName || '', loadingName ? isLoading : false);
  
  // Handle error notifications
  const { formattedError } = useErrorNotificationHook(
    isError,
    errorMessage,
    typedError,
    true // Auto show toast
  );
  
  return { formattedError };
}; 